import { useState } from 'react';
import type { ProjectExtended, ModuleData } from '../../projects/types';
import { formatWorkOrderNumber } from '../../projects/types';
import { PipelineStatusPanel } from './PipelineStatusPanel';
import { SyntaxMonitorPanel } from './SyntaxMonitorPanel';

interface ProjectExpandableRowProps {
  project: ProjectExtended;
  onEdit: () => void;
  onDelete: () => void;
  onRetryPipeline?: () => void;
  onRetrySyntax?: () => void;
  /** True when this row is in "confirm" state (first click done, waiting for second click) */
  isDeleting?: boolean;
  /** True when this specific project is being deleted via API call */
  isBeingDeletedByApi?: boolean;
  retryingPipeline?: boolean;
  retrySyntaxing?: boolean;
}

/**
 * Fila expandible de proyecto para el panel de SuperAdmin
 *
 * @remarks
 * Muestra información resumida y permite expandir para ver:
 * - Estado del pipeline
 * - Estadísticas de red
 * - Datos por módulo
 * - Estado de uso
 */
export function ProjectExpandableRow({
  project,
  onEdit,
  onDelete,
  onRetryPipeline,
  onRetrySyntax,
  isDeleting = false,
  isBeingDeletedByApi = false,
  retryingPipeline = false,
  retrySyntaxing = false,
}: ProjectExpandableRowProps): JSX.Element {
  const [isExpanded, setIsExpanded] = useState(false);

  const toggleExpanded = () => setIsExpanded(!isExpanded);

  // Show "Confirmar" text when in confirmation state (first click done)
  const showConfirmingState = isDeleting;
  // Show loading state when API deletion is in progress
  const showLoadingState = isBeingDeletedByApi;
  // Only disable this row's buttons when THIS project is being deleted by API
  const isDisabled = isBeingDeletedByApi;

  // Determinar color del estado de uso
  const usageStatusColors: Record<string, string> = {
    active: 'var(--color-success, #28a745)',
    low: 'var(--color-warning, #ffc107)',
    inactive: 'var(--text-tertiary, #6c757d)',
    abandoned: 'var(--color-error, #dc3545)',
  };

  const usageStatusLabels: Record<string, string> = {
    active: 'Activo',
    low: 'Bajo uso',
    inactive: 'Inactivo',
    abandoned: 'Abandonado',
  };

  return (
    <>
      {/* Fila principal */}
      <tr className={`table-row expandable-row ${isExpanded ? 'expanded' : ''}`}>
        <td className="table-cell-expand">
          <button
            type="button"
            className="btn-expand"
            onClick={toggleExpanded}
            title={isExpanded ? 'Contraer' : 'Expandir'}
            disabled={isDisabled}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              className={`expand-icon ${isExpanded ? 'rotated' : ''}`}
            >
              <path
                d="M6 4L10 8L6 12"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </td>
        <td className="table-cell-ot">
          <span className="ot-badge">
            {formatWorkOrderNumber(project.clientNumber || 0, project.projectNumber || 0)}
          </span>
        </td>
        <td className="table-cell-name">{project.name}</td>
        <td className="table-cell-client">{project.tenant.name}</td>
        <td className="table-cell-pipeline">
          <span className={`pipeline-badge status-${project.pipeline.overallStatus}`}>
            {project.pipeline.progressPercent}%
          </span>
        </td>
        <td className="table-cell-network">
          <span className="network-stat">{project.network.edgesCount.toLocaleString('es-AR')}</span>
        </td>
        <td className="table-cell-modules">
          <span className="modules-stat">
            {project.modulesWithData}/{project.modulesContracted}
          </span>
        </td>
        <td className="table-cell-usage">
          <span
            className="usage-badge"
            style={{ color: usageStatusColors[project.usage.status] }}
          >
            {usageStatusLabels[project.usage.status]}
          </span>
        </td>
        <td className="table-cell-actions">
          <button
            type="button"
            className="btn-table-action btn-edit"
            onClick={onEdit}
            title="Editar proyecto"
            disabled={isDisabled}
          >
            Editar
          </button>
          <button
            type="button"
            className={`btn-table-action btn-delete ${showConfirmingState ? 'confirming' : ''} ${showLoadingState ? 'loading' : ''}`}
            onClick={onDelete}
            title={showLoadingState ? 'Eliminando...' : showConfirmingState ? 'Confirmar eliminación' : 'Eliminar proyecto'}
            disabled={isDisabled}
          >
            {showLoadingState ? (
              <>
                <span className="spinner" />
                Eliminando...
              </>
            ) : showConfirmingState ? (
              'Confirmar'
            ) : (
              'Eliminar'
            )}
          </button>
        </td>
      </tr>

      {/* Panel expandido */}
      {isExpanded && (
        <tr className="expanded-panel-row">
          <td colSpan={9}>
            <div className="expanded-panel">
              {/* Grid de 3 columnas */}
              <div className="expanded-grid">
                {/* Columna 1: Pipeline */}
                <div className="expanded-section">
                  <PipelineStatusPanel
                    pipeline={project.pipeline}
                    onRetry={onRetryPipeline}
                    retrying={retryingPipeline}
                    onRetrySyntax={onRetrySyntax}
                    retrySyntaxing={retrySyntaxing}
                  />
                  <SyntaxMonitorPanel
                    projectId={project.id}
                    onRetrySyntax={onRetrySyntax}
                    retrySyntaxing={retrySyntaxing}
                  />
                </div>

                {/* Columna 2: Red y Sintaxis */}
                <div className="expanded-section">
                  <div className="section-header">
                    <h4 className="section-title">Red Urbana</h4>
                    {project.network.version > 0 && (
                      <span className="section-badge">v{project.network.version}</span>
                    )}
                  </div>
                  <div className="stats-grid">
                    <div className="stat-item">
                      <span className="stat-value">{project.network.nodesCount.toLocaleString('es-AR')}</span>
                      <span className="stat-label">Nodos</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-value">{project.network.edgesCount.toLocaleString('es-AR')}</span>
                      <span className="stat-label">Edges</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-value">{project.network.totalLengthKm.toFixed(1)} km</span>
                      <span className="stat-label">Longitud</span>
                    </div>
                  </div>

                  <div className="section-header" style={{ marginTop: '16px' }}>
                    <h4 className="section-title">Sintaxis Espacial</h4>
                    <span className={`syntax-status status-${project.syntax.status}`}>
                      {project.syntax.status === 'valid' && '● Válida'}
                      {project.syntax.status === 'stale' && '⚠ Desactualizada'}
                      {project.syntax.status === 'calculating' && '◐ Calculando'}
                      {project.syntax.status === 'pending' && '○ Pendiente'}
                      {project.syntax.status === 'error' && '✕ Error'}
                    </span>
                  </div>
                  <div className="stats-grid">
                    <div className="stat-item">
                      <span className="stat-value">v{project.syntax.version}</span>
                      <span className="stat-label">Versión</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-value">{project.syntax.coverage}%</span>
                      <span className="stat-label">Cobertura</span>
                    </div>
                  </div>
                </div>

                {/* Columna 3: Módulos */}
                <div className="expanded-section">
                  <div className="section-header">
                    <h4 className="section-title">Módulos</h4>
                    <span className="section-badge">
                      {project.modulesWithData}/{project.modulesContracted}
                    </span>
                  </div>
                  <div className="modules-list">
                    {project.modules.length === 0 ? (
                      <p className="no-modules">Sin módulos contratados</p>
                    ) : (
                      project.modules.map((module: ModuleData) => (
                        <div
                          key={module.moduleId}
                          className={`module-item ${module.hasData ? 'has-data' : 'no-data'}`}
                        >
                          <div className="module-header">
                            <span className="module-name">{module.moduleName}</span>
                            <span className="module-count">
                              {module.entitiesCount.toLocaleString('es-AR')}
                            </span>
                          </div>
                          {module.entities.length > 0 && (
                            <div className="module-entities">
                              {module.entities.map((entity) => (
                                <span key={entity.type} className="entity-tag">
                                  {entity.typeName}: {entity.count}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>

              {/* Fila inferior: Info contractual */}
              <div className="expanded-footer">
                <div className="footer-item">
                  <span className="footer-label">Creado</span>
                  <span className="footer-value">
                    {new Date(project.createdAt).toLocaleDateString('es-AR')}
                  </span>
                </div>
                {project.startDate && (
                  <div className="footer-item">
                    <span className="footer-label">Inicio servicio</span>
                    <span className="footer-value">
                      {new Date(project.startDate).toLocaleDateString('es-AR')}
                    </span>
                  </div>
                )}
                {project.endDate && (
                  <div className="footer-item">
                    <span className="footer-label">Fin servicio</span>
                    <span className="footer-value">
                      {new Date(project.endDate).toLocaleDateString('es-AR')}
                      {project.daysRemaining !== null && (
                        <span className={`days-remaining ${project.daysRemaining <= 30 ? 'warning' : ''}`}>
                          ({project.daysRemaining}d)
                        </span>
                      )}
                    </span>
                  </div>
                )}
                <div className="footer-item">
                  <span className="footer-label">Usuarios</span>
                  <span className="footer-value">{project.usersCount}</span>
                </div>
                {project.usage.lastAccessedAt && (
                  <div className="footer-item">
                    <span className="footer-label">Último acceso</span>
                    <span className="footer-value">
                      {new Date(project.usage.lastAccessedAt).toLocaleDateString('es-AR')}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}
